import { auth } from "@/lib/firebase";
import * as Sentry from "@sentry/react";
import {
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  UserCredential,
  getMultiFactorResolver,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from "firebase/auth";

// Constants
const RECAPTCHA_CONTAINER_ID = "recaptcha-container";

export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
    window.location.href = "/login";
  } catch (error) {
    throw error;
  }
};

// Error messages mapping
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  // Email/Password errors
  "auth/invalid-email": "올바른 이메일 형식이 아닙니다.",
  "auth/missing-email": "이메일을 입력해 주세요.",
  "auth/missing-password": "비밀번호를 입력해 주세요.",
  "auth/wrong-password": "이메일 또는 비밀번호가 일치하지 않습니다.",
  "auth/invalid-credential": "이메일 또는 비밀번호가 일치하지 않습니다.",
  "auth/invalid-login-credentials": "이메일 또는 비밀번호가 일치하지 않습니다.",
  "auth/user-not-found": "이메일 또는 비밀번호가 일치하지 않습니다.",

  // Account status errors
  "auth/user-disabled": "비활성화된 계정입니다. 관리자에게 문의해 주세요.",
  "auth/admin-restricted-operation":
    "등록되지 않은 사용자입니다. 관리자에게 문의해 주세요.",

  // MFA errors
  "auth/multi-factor-auth-required": "2단계 인증이 필요합니다.",
  "auth/invalid-verification-code": "인증코드가 일치하지 않습니다.",
  "auth/code-expired":
    "인증 시도 횟수를 초과하여 세션이 만료되었습니다.\n새로고침 후 다시 로그인해 주세요.",
  "auth/session-expired": "인증 세션이 만료되었습니다. 다시 시도해주세요.",

  // Rate limiting
  "auth/too-many-requests":
    "너무 많은 시도가 있었습니다. 잠시 후 다시 시도해 주세요.",
};

export const getAuthErrorMessage = (error: any): string => {
  const errorCode = error?.code || "";
  const knownMessage = AUTH_ERROR_MESSAGES[errorCode];

  if (knownMessage) {
    // 매핑된 에러(잘못된 비밀번호 등)는 사용자 실수 가능성이 높아 콘솔에만 남긴다
    console.warn(`[auth] ${errorCode}: ${error?.message ?? ""}`);
    return knownMessage;
  }

  // 매핑에 없는 에러는 generic 메시지로 뭉개지므로 원인 추적을 위해 Sentry에 남긴다
  console.error("[auth] unmapped auth error:", error);
  Sentry.captureException(error, {
    tags: { feature: "auth" },
    extra: { errorCode, errorMessage: error?.message },
  });

  return "로그인에 실패했습니다. 다시 시도해 주세요.";
};

// MFA types for better type safety
export interface MfaSession {
  resolver: MultiFactorResolver;
  verificationId?: string;
  selectedHint?: MultiFactorInfo;
}

// reCAPTCHA Manager
class RecaptchaManager {
  private static instance: RecaptchaVerifier | null = null;
  private static isRendered: boolean = false;

  static async get(): Promise<RecaptchaVerifier> {
    // If already rendered and instance exists, return it
    if (this.instance && this.isRendered) {
      return this.instance;
    }

    // Clean up any existing instance
    await this.cleanup();

    // Create new instance
    this.instance = new RecaptchaVerifier(auth, RECAPTCHA_CONTAINER_ID, {
      size: "invisible",
      "expired-callback": () => {
        console.warn("reCAPTCHA expired");
        this.cleanup();
      },
    });

    try {
      await this.instance.render();
      this.isRendered = true;
    } catch (error: any) {
      // If already rendered error, just return the instance
      if (error.message?.includes("already been rendered")) {
        console.warn("reCAPTCHA already rendered, using existing instance");
        this.isRendered = true;
      } else {
        throw error;
      }
    }

    return this.instance;
  }

  static async cleanup(): Promise<void> {
    if (!this.instance) return;

    try {
      this.instance.clear();
    } catch (error) {
      console.warn("Error clearing reCAPTCHA:", error);
    } finally {
      this.instance = null;
      this.isRendered = false;

      // Also clear the DOM element
      const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
      if (container) {
        container.innerHTML = "";
      }
    }
  }
}

export const getRecaptchaVerifier = () => RecaptchaManager.get();
export const clearRecaptchaVerifier = () => RecaptchaManager.cleanup();

/**
 * Starts MFA verification process and gets verification ID
 * @param resolver The MFA resolver
 * @param recaptchaVerifier The RecaptchaVerifier instance
 * @param hintIndex Index of the hint to use (default 0)
 * @returns Promise with phone number, verification ID, and selected hint
 */
// Helper function to extract phone number from hint
const extractPhoneNumber = (hint: MultiFactorInfo): string => {
  return "phoneNumber" in hint ? (hint as any).phoneNumber : "Unknown";
};

export const startMfaVerification = async (
  resolver: MultiFactorResolver,
  hintIndex: number = 0,
): Promise<{
  phoneNumber: string;
  verificationId: string;
  selectedHint: MultiFactorInfo;
}> => {
  // Validate inputs
  if (!resolver) throw new Error("No MFA resolver provided");
  if (hintIndex >= resolver.hints.length) throw new Error("Invalid hint index");

  const selectedHint = resolver.hints[hintIndex];
  const recaptchaVerifier = await getRecaptchaVerifier();

  try {
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const phoneInfoOptions = {
      multiFactorHint: selectedHint,
      session: resolver.session,
    };

    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      phoneInfoOptions,
      recaptchaVerifier,
    );

    return {
      phoneNumber: extractPhoneNumber(selectedHint),
      verificationId,
      selectedHint,
    };
  } catch (error) {
    await RecaptchaManager.cleanup();
    throw error;
  }
};

/**
 * Updates the current user's password
 * @param newPassword The new password
 * @returns Promise that resolves when password is updated
 */
export const updateUserPassword = async (
  newPassword: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: "사용자를 찾을 수 없습니다." };
    }

    await updatePassword(user, newPassword);
    return { success: true };
  } catch (error: any) {
    // Handle specific Firebase errors
    if (error.code === "auth/weak-password") {
      return {
        success: false,
        error: "비밀번호가 너무 약합니다. 더 강력한 비밀번호를 사용해주세요.",
      };
    } else if (error.code === "auth/requires-recent-login") {
      return {
        success: false,
        error: "보안을 위해 다시 로그인한 후 비밀번호를 변경해주세요.",
      };
    }

    return {
      success: false,
      error: error.message || "비밀번호 변경에 실패했습니다.",
    };
  }
};

// MFA types for better type safety
export interface MfaSession {
  resolver: MultiFactorResolver;
  verificationId?: string;
  selectedHint?: MultiFactorInfo;
}

/**
 * Signs in with email and password, handling MFA if required
 * @param email The user's email address
 * @param password The user's password
 * @returns A Promise that resolves with the UserCredential or MFA info
 */
export const signInWithPassword = async (
  email: string,
  password: string,
): Promise<{
  success: boolean;
  requiresMfa?: boolean;
  verificationId?: string;
  resolver?: MultiFactorResolver;
  userCredential?: UserCredential;
  error?: string;
}> => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );

    return { success: true, userCredential };
  } catch (error: any) {
    if (error.code === "auth/multi-factor-auth-required") {
      // MFA is required
      const multiFactorError = error as MultiFactorError;
      const resolver = getMultiFactorResolver(auth, multiFactorError);

      try {
        const startMfaVerificationResult = await startMfaVerification(resolver);

        return {
          success: false,
          requiresMfa: true,
          verificationId: startMfaVerificationResult.verificationId,
          resolver,
        };
      } catch (mfaError: any) {
        // mfaError.message가 있으면 getAuthErrorMessage를 타지 않아 로깅이 누락되므로 여기서 직접 남긴다
        console.error("[auth] MFA verification failed:", mfaError);
        Sentry.captureException(mfaError, {
          tags: { feature: "auth", stage: "mfa-verification" },
          extra: { errorCode: mfaError?.code, errorMessage: mfaError?.message },
        });

        return {
          success: false,
          error: mfaError.message || getAuthErrorMessage(mfaError),
        };
      }
    }

    // Other errors
    return {
      success: false,
      error: getAuthErrorMessage(error),
    };
  }
};

/**
 * Gets available MFA hints (phone numbers)
 * @param resolver The MFA resolver
 * @returns Array of MFA hints
 */
export const getMfaHints = (
  resolver: MultiFactorResolver | null,
): MultiFactorInfo[] => {
  if (!resolver) return [];
  return resolver.hints as MultiFactorInfo[];
};

/**
 * Completes MFA sign-in with verification code
 * @param resolver The MFA resolver
 * @param verificationId The verification ID from startMfaVerification
 * @param verificationCode The 6-digit verification code from SMS/TOTP
 * @returns A Promise that resolves with the UserCredential
 */
// src/services/authService.ts의 completeMfaSignIn 함수에 추가
export const completeMfaSignIn = async (
  resolver: MultiFactorResolver,
  verificationId: string,
  verificationCode: string,
): Promise<UserCredential> => {
  if (!resolver || !verificationId) {
    throw new Error("MFA session not initialized or verification ID missing");
  }

  // try {
  const credential = PhoneAuthProvider.credential(
    verificationId,
    verificationCode,
  );

  const assertion = PhoneMultiFactorGenerator.assertion(credential);

  const userCredential = await resolver.resolveSignIn(assertion);

  return userCredential;
  // } catch (error: any) {
  //   console.log("inside catch");
  //   if (error.code === "auth/invalid-verification-code") {
  //     throw new Error("인증코드가 일치하지 않습니다.");
  //   } else if (error.code === "auth/code-expired") {
  //     throw new Error(
  //       "인증 시도 횟수를 초과하여 세션이 만료되었습니다. 새로고침 후 다시 로그인해 주세요.",
  //     );
  //   } else{
  //     // TODO: 추후 수정
  //     throw new Error(
  //       "인증 시도 횟수를 초과하여 세션이 만료되었습니다. 새로고침 후 다시 로그인해 주세요.",
  //     );
  //   }

  //   throw error;
  // }
};
